
import { Button } from "@/components/ui/button";
import { useState, useEffect } from 'react';
import { toast } from "sonner";
import { 
  detectTelegramEnvironment, 
  getTelegramWalletInstructions,
  checkForConnectedWallet,
  type TelegramWalletInfo 
} from '@/lib/mobileWalletUtils';
import { 
  generateEncryptionKeys, 
  createPhantomConnectUrl, 
  openPhantomInTelegram 
} from '@/lib/phantomConnectUtils';


interface UnifiedConnectWalletProps {
  onConnectWallet: () => void;
}

const UnifiedConnectWallet = ({ onConnectWallet }: UnifiedConnectWalletProps) => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [telegramWalletInfo, setTelegramWalletInfo] = useState<TelegramWalletInfo | null>(null);
  const [canConnect, setCanConnect] = useState(false);
  
  useEffect(() => {
    const hasIntent = localStorage.getItem('cipher-intent') === 'true';
    setCanConnect(hasIntent);
    
    // Initialize Telegram environment detection
    const walletInfo = detectTelegramEnvironment();
    setTelegramWalletInfo(walletInfo);
    
    console.log('Unified Wallet Telegram Environment:', walletInfo);
    
    // Check for existing wallet connection
    const connectedWallet = checkForConnectedWallet();
    if (connectedWallet) {
      console.log('Found connected wallet, completing flow:', connectedWallet);
      onConnectWallet();
    }
    
    return () => {
      localStorage.removeItem('cipher-intent');
    };
  }, [onConnectWallet]);

  const handleConnectClick = async () => {
    if (isConnecting) return;
    
    try {
      setIsConnecting(true);
      console.log('Connecting to Phantom wallet');
      
      if (!telegramWalletInfo?.isTelegram) {
        toast.error("This feature is designed for Telegram Mini Apps");
        return;
      }
      
      // Generate encryption keys
      const { publicKey, privateKey } = generateEncryptionKeys();
      
      // Store keys locally (in production, private key should be server-side)
      localStorage.setItem('phantom_public_key', publicKey);
      localStorage.setItem('phantom_private_key', privateKey);
      
      // Create the callback URL
      const callbackUrl = `${window.location.origin}/phantom-callback`;
      const appUrl = window.location.origin;
      
      // Create Phantom connect URL
      const connectUrl = createPhantomConnectUrl({
        appUrl,
        redirectUrl: callbackUrl,
        publicKey
      });
      
      console.log('Opening Phantom connect URL:', connectUrl);
      
      toast.info("Opening Phantom app. Please approve the connection and return to complete setup.");
      
      // Open Phantom using Telegram's method
      openPhantomInTelegram(connectUrl);
      
    } catch (error) {
      console.error("Error connecting wallet:", error);
      toast.error("Failed to connect Phantom wallet. Please try again.");
    } finally {
      setIsConnecting(false);
    }
  };
  
  if (!canConnect) {
    return (
      <div className="text-center space-y-6">
        <h2 className="text-2xl font-patrick text-tan">Access Denied</h2>
        <p className="text-lg text-tan">You must access this page through the Brotocol.</p>
        <div className="flex justify-center mt-8">
          <Button 
            onClick={() => window.location.href = '/brotocol'}
            className="bg-tan text-navy hover:bg-tan-dark px-8 py-6 text-xl"
          >
            Go to Brotocol
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="text-center space-y-6">
      {/* Solana Support Banner */}
      <div className="mb-6 p-4 bg-tan text-navy-dark rounded-lg">
        <div className="flex items-center justify-center space-x-2 mb-2">
          <img 
            src="/lovable-uploads/clr_4.png" 
            alt="Bromentum Logo" 
            className="w-6 h-6 object-contain"
          />
          <span className="font-bold">☀️ Solana Support</span>
        </div>
        <p className="text-sm font-bold">Connect with Phantom wallet to earn your BroCode on Solana!</p>
      </div>

      <h2 className="text-2xl font-patrick text-tan">Step 1: Connect Your Phantom Wallet</h2>
      
      {/* Telegram-specific information */}
      {telegramWalletInfo && (
        <div className="bg-navy-dark p-4 rounded-md my-4">
          <p className="text-sm text-tan mb-2">📱 Telegram Mini App Environment</p>
          <p className="text-sm text-tan">
            {getTelegramWalletInstructions(telegramWalletInfo)}
          </p>
        </div>
      )}
      
      <div className="flex justify-center mt-8">
        <Button 
          onClick={handleConnectClick}
          disabled={isConnecting || !telegramWalletInfo?.isTelegram}
          className="bg-tan text-navy hover:bg-tan-dark px-8 py-6 text-xl"
        >
          {isConnecting ? 'Opening Phantom...' : 'Connect Phantom Wallet'}
        </Button>
      </div>
      
      {isConnecting && (
        <p className="text-sm text-tan animate-pulse">
          Opening Phantom wallet connection...
        </p>
      )}
      
      {!telegramWalletInfo?.isTelegram && (
        <p className="text-sm text-red-400">
          This feature requires Telegram Mini App environment
        </p>
      )}
    </div>
  );
};

export default UnifiedConnectWallet;
